
import { polyfillEd25519 } from './lib/webcrypto-ed25519-polyfill/browser.js';
try {
polyfillEd25519();
}catch{
    alert("failed to initialize web crypto. the app won't work properly. peut-être you are using an insecure origin?")
}

import { idbGet, idbPut, openDb } from './util/IDBUtils';
import { MemoRemoteInterface } from './IMemoRemote';
import { WebSocketRpcClient } from './lib/rpc/WebsocketRpcClient';
import { IPinboardServer } from './lib/rpcInterfaces/IPinboardServer';
import { ICrypto, RpcOp, Signature } from './lib/rpc/rpc';
import { toBase64Url as b64encode, toBuffer as b64decode } from "b64u-lite";

import {DefaultSubtleCrypto, Ed25519PrivateToPublic} from './lib/rpc/crypto';
import {crypto} from "ws-helper";
import {signal} from "./lib";

export class BrowserCrypto extends DefaultSubtleCrypto {
    indexeddb : IDBDatabase;
    initialized: boolean = false;
    
    async init() {
        if (this.initialized) return;
        this.indexeddb = await openDb("browserCrypto", 1, function (e) {
            if (e.oldVersion < 1) {
                this.result.createObjectStore("keys", {keyPath: ["type", "id"]});
            }
        });
        let key = await idbGet<any>(this.indexeddb, 'keys', ['nonexportableClientKey', 1]);
        if (key) {
            await super.init(false, key.keyObject as CryptoKeyPair);
        } else if (localStorage._nonexportableClientKey) {
            // recover the key from localStorage, as localStorage doesn't get evicted as often as indexedDB
            const privKey = await crypto.subtle.importKey("jwk", JSON.parse(localStorage._nonexportableClientKey),
                {name: 'Ed25519'},
                true,
                ["sign"]);
            await super.init(true, {privateKey: privKey, publicKey: await Ed25519PrivateToPublic(privKey)});
        } else {
            await super.init(false);
        }
        this.initialized = true;
    }
    async storeClientKey(keyPair: CryptoKeyPair): Promise<void> {
        await idbPut(this.indexeddb, 'keys', {type: 'nonexportableClientKey', id: 1, keyObject: keyPair });
        try {  //  /o\
            // this only works on polyfilled keys...
            (keyPair.privateKey as any).extractable = true;
            localStorage._nonexportableClientKey = JSON.stringify(await crypto.subtle.exportKey('jwk', keyPair.privateKey));
            (keyPair.privateKey as any).extractable = false;
        } catch { }
    }
}

export class PinboardAppNativeCrypto extends DefaultSubtleCrypto {
    indexeddb : IDBDatabase;
    initialized: boolean = false;

    async init() {
        if (this.initialized) return;
        this.clientKeyPublicKeyString = globalThis['pinboardNative'].clientKeyPublicKeyString();
        this.clientKeyPublicKeyRaw = b64decode(this.clientKeyPublicKeyString);
        console.log("Hello, my public key is ",this.clientKeyPublicKeyString);
        await this.initSessionKey();
        this.initialized = true;
    }

    async _signDataWithClientKey(wrappedData: Uint8Array): Promise<Uint8Array> {
        return b64decode(globalThis['pinboardNative'].signWithClientKey_b64(b64encode(wrappedData)));
    }

    static isAvailable() {
        return 'pinboardNative' in globalThis;
    }
}

export class WebSocketRemote extends WebSocketRpcClient implements MemoRemoteInterface {
    public loginUser: any = null;
    public pb_notimeout: IPinboardServer;
    public pb: IPinboardServer;

    readonly connectionEstablished = signal<() => void>();

    constructor(crypto: ICrypto, defaultWebsocketTarget?: string) {
        const wsTarget = localStorage.websocketTarget || defaultWebsocketTarget || `ws${location.protocol.substring(4)}//${location.host}`;
        super(wsTarget, crypto);
        this.pb = this.getProxy('pb', 'pb', 'pb', undefined, parseInt(localStorage.rpcTimeout) || 30000);
        this.pb_notimeout = this.getProxy('pb', 'pb', 'pb', undefined, parseInt(localStorage.rpcTimeout) || 0);
        this.connected.connect(() => this.connectionEstablished.emit());
    }

    async login(username: string, password: string): Promise<any> {
        return await this.pb_notimeout.login(username, password);
    }

    async register(username: string, password: string): Promise<any> {
        return await this.pb_notimeout.register(username, password);
    }

    onSendChatMessage(message: string) {
        this.sendRpcCallNoResponse(RpcOp.METHODCALL, "pb", "pb", "pb", "send_chat_message", [{ "chat": { "message": message, } }], {});
    }

    async listDir(dir: string): Promise<string[]> {
        return (await this.pb_notimeout.list_dir(dir)).files;
    }

    async getUpdates(docVersions: [string,number][], subscribeToUpdates = true): Promise<{docs: {document:string, items?: any[], cursor?: number, server_error?: string}[]}> {
        return await this.pb_notimeout.get_updates(docVersions, subscribeToUpdates);
    }
    
    onSendObjects(documentId: string, items: { [key: string]: any; }[], command: string | undefined): Promise<{mt: number, cursor: number | null}> {
        return this.pb_notimeout.send_objects({
            "document": documentId,
            "items": items,
            "command": command,
        });
    }
    async retrieveBlob(blobHash: string): Promise<Uint8Array> {
        // the retry logic applies in the following situation:
        // 1. - client A adds an image file to local blobStore
        // 2. - client A's blobStore starts uploading image file
        // 3. - client A adds `image` reference to MemoItem
        // 4. - client A sends MemoItem to remote
        // 5. - client B receives MemoItem from remote
        // 6. - client B tries to retrieve `image` from blobStore
        // 7. - client B's blobStore fails to fetch image file from remote, because the upload started in step 2 isn't completed yet
        // 8. - client A's blobStore is done uploading
        // 9. - client B's blobStore retries, succeeds now
        // 10. - image rendered successfully in client B

        // might be moved into a different place later on (either blobStore or MemoItem.render)

        var retries = 0;
        while (true) {
            try {
                var response = await this.pb.retrieve_blob(blobHash);
                console.log(response);
                if (!response.blob) throw `retrieveBlob failed`;
                return response.blob;
            } catch (ex) {
                if (retries++ > 2) throw ex;
                else console.log("retrieveBlob failed, retrying ", retries, ex);
            }
        }
    }
    async checkBlobs(blobHashes: string[]): Promise<number[]> {
        return (await this.pb.check_blobs(blobHashes)).state;
    }
    async storeBlob(blob: Uint8Array): Promise<string> {
        if (blob instanceof ArrayBuffer) blob = new Uint8Array(blob);
        return (await this.pb.store_blob(blob)).blob_uploaded;
    }
}
